// Spend dashboard page (v2 Step 7).
// Layout: cap ring + summary stats up top, daily bar chart middle,
// per-trace drill-down for the selected day at the bottom.

import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { CapProgressRing } from "@/components/spend/CapProgressRing";
import { DailySpendChart } from "@/components/spend/DailySpendChart";
import { TraceList } from "@/components/spend/TraceList";
import {
  fetchCapStatus,
  fetchSpend,
  fetchTracesForDay,
} from "@/services/api/spend";

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

export function Spend() {
  const [selectedDay, setSelectedDay] = useState<string>(isoToday());

  const capQuery = useQuery({
    queryKey: ["spend", "cap"],
    queryFn: fetchCapStatus,
  });

  const spendQuery = useQuery({
    queryKey: ["spend", "month"],
    queryFn: () => fetchSpend("day"),
  });

  const tracesQuery = useQuery({
    queryKey: ["spend", "traces", selectedDay],
    queryFn: () => fetchTracesForDay(selectedDay),
    enabled: Boolean(selectedDay),
  });

  const totals = spendQuery.data?.totals;
  const buckets = useMemo(() => spendQuery.data?.buckets ?? [], [spendQuery.data]);

  return (
    <div className="mx-auto w-full max-w-5xl px-6 py-10">
      <header className="mb-8">
        <p className="smallcaps text-oxblood-500">Spend</p>
        <h1 className="font-display text-3xl text-espresso-700">
          Your usage this month
        </h1>
        <p className="mt-1 text-sm text-warmgray-500">
          Per-trace tool calls, tokens, and estimated cost. Updates as
          conversations complete.
        </p>
      </header>

      <section className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="rounded border border-ruleline bg-parchment-50 p-5">
          {capQuery.isLoading ? (
            <p className="text-sm italic text-warmgray-400">Loading cap…</p>
          ) : capQuery.data ? (
            <CapProgressRing status={capQuery.data} />
          ) : (
            <p className="text-sm italic text-warmgray-400">
              Cap unavailable
            </p>
          )}
        </div>

        <div className="md:col-span-2 grid grid-cols-3 gap-4">
          <div className="rounded border border-ruleline bg-parchment-50 p-4">
            <p className="text-[0.65rem] uppercase tracking-wider text-warmgray-500">
              Traces
            </p>
            <p className="mt-1 text-2xl font-display text-espresso-700">
              {totals?.trace_count ?? "—"}
            </p>
          </div>
          <div className="rounded border border-ruleline bg-parchment-50 p-4">
            <p className="text-[0.65rem] uppercase tracking-wider text-warmgray-500">
              Tool calls
            </p>
            <p className="mt-1 text-2xl font-display text-espresso-700">
              {totals?.tool_calls ?? "—"}
            </p>
          </div>
          <div className="rounded border border-ruleline bg-parchment-50 p-4">
            <p className="text-[0.65rem] uppercase tracking-wider text-warmgray-500">
              Tokens
            </p>
            <p className="mt-1 text-2xl font-display text-espresso-700">
              {totals ? totals.total_tokens.toLocaleString() : "—"}
            </p>
          </div>
        </div>
      </section>

      <section className="mt-8">
        <h2 className="mb-3 font-serif text-lg text-espresso-700">
          Daily spend
        </h2>
        {spendQuery.isLoading ? (
          <p className="text-sm italic text-warmgray-400">Loading chart…</p>
        ) : (
          <DailySpendChart
            buckets={buckets}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
          />
        )}
      </section>

      <section className="mt-8">
        <h2 className="mb-3 font-serif text-lg text-espresso-700">
          Traces on {selectedDay}
        </h2>
        <TraceList
          day={selectedDay}
          traces={tracesQuery.data?.traces ?? []}
          isLoading={tracesQuery.isLoading}
        />
      </section>
    </div>
  );
}
